import React, {useMemo, useRef} from 'react';
import clsx from 'clsx';
import {nanoid} from 'nanoid';
import {Button} from '@mui/material';
import {Dialog, type DialogProps} from '@netx/core-components/Dialog';
import {Form, type FormProps} from '@netx/core-components/Form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useMediaQuery} from '@netx/core-components/hooks/useMediaQuery';
import SubmitButton from '@netx/core-components/SubmitButton';
import {
	composeClasses,
	styled,
	useTheme,
	type WithClasses,
} from '@netx/core-components/Theme';
import {renderChildrenOrChildrenFunction} from '@netx/core-next/utils/react';
import {i18n} from '@netx/core/i18n';
import {
	FORM_DIALOG_CLASS_NAME,
	formDialogClasses,
	type FormDialogClasses,
	getFormDialogUtilityClass,
} from './formDialogClasses';

type FormDialogUtiltiyClassProps = WithClasses<
	FormDialogClasses,
	{
		disabled?: boolean;
	}
>;

const useUtilityClasses = (ownerState: FormDialogUtiltiyClassProps) => {
	const {classes, disabled} = ownerState;
	const slots = {
		root: ['root', disabled && 'disabled'],
		actions: ['actions'],
		cancelButton: ['cancelButton'],
		content: ['content'],
		form: ['form'],
		progress: ['progress'],
		submitButton: ['submitButton'],
	};
	return composeClasses(slots, getFormDialogUtilityClass, classes);
};

const FormDialogRoot = styled(Dialog, {
	name: FORM_DIALOG_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {disabled} = ownerState as FormDialogUtiltiyClassProps;
	return {
		...(disabled
			? {
					[`& .${formDialogClasses.form}`]: {
						...theme.mixins.disabled,
						pointerEvents: 'none !important',
					},
			  }
			: null),
	};
});

const FormDialogForm = styled(Form, {
	name: FORM_DIALOG_CLASS_NAME,
	slot: 'Form',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	padding: theme.spacing(1, 0),
}));

const FormDialogCancelButton = styled(Button, {
	name: FORM_DIALOG_CLASS_NAME,
	slot: 'CancelButton',
})(({theme}) => ({
	// marginRight: theme.spacing(1),
	[`& + .${formDialogClasses.submitButton}`]: {
		marginLeft: theme.spacing(1),
	},
}));

const FormDialogSubmitButton = styled(SubmitButton, {
	name: FORM_DIALOG_CLASS_NAME,
	slot: 'SubmitButton',
})(({theme}) => ({}));

export interface FormDialogProps
	extends FormProps,
		Pick<
			DialogProps,
			'onClose' | 'open' | 'fullSize' | 'fullScreen' | 'fullScreenWhenMobile'
		>,
		WithClasses<FormDialogClasses> {
	disabled?: boolean;
	disableSubmit?: boolean;
	disableFormPadding?: boolean;
	error?: React.ReactNode;
	formId?: string;
	cancelLabel?: string;
	confirmLabel?: string;
	hideCancel?: boolean;
	hideSubmit?: boolean;
	isDirty?: boolean;
	isValid?: boolean;
	progress?: number;
	requiresChanges?: boolean;
	isSubmitting?: boolean;
	DialogProps: Partial<Omit<DialogProps, 'classes' | 'onClose' | 'open'>>;

	dialogActions?:
		| React.ReactNode
		| ((props: {
				formId: string;
				formRef: React.RefObject<HTMLFormElement | null>;
				isSubmitting: boolean;
				submitEnabled: boolean;
		  }) => React.ReactNode);
}

/**
 * Standard dialog for forms
 */
export const FormDialogPrimitive = ({
	children,
	classes: _classes,
	className,

	cancelLabel,
	disabled,
	disableSubmit,
	disableFormPadding,
	error,
	fullScreenWhenMobile = true,
	fullScreen,
	fullSize,
	confirmLabel,
	formId: _formId,
	hideCancel,
	hideSubmit,
	isDirty,
	isSubmitting,
	isValid,
	open,
	onClose,
	progress,
	requiresChanges = true,
	title,
	DialogProps,
	dialogActions,

	...props
}: FormDialogProps) => {
	const formId = useMemo(() => _formId || `form-${nanoid()}`, [_formId]);
	cancelLabel = cancelLabel != null ? cancelLabel : i18n.t('i18n.cancel');
	confirmLabel = confirmLabel != null ? confirmLabel : i18n.t('i18n.submit');

	const formRef = useRef<HTMLFormElement | null>(null);

	const theme = useTheme();

	const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

	const ownerState: FormDialogUtiltiyClassProps = {
		classes: _classes,
		disabled: disabled || isSubmitting,
	};

	const classes = useUtilityClasses(ownerState);

	const submitEnabled =
		!disableSubmit &&
		!disabled &&
		!isSubmitting &&
		isValid === true &&
		(!requiresChanges || isDirty === true);

	const handleCancel = useConstCallback((event: any) => {
		onClose?.(event, 'escapeKeyDown');
	});

	return (
		<FormDialogRoot
			error={error}
			fullScreenWhenMobile={fullScreenWhenMobile}
			fullScreen={fullScreen}
			fullSize={fullSize}
			fullWidth
			maxWidth="sm"
			dialogActions={
				dialogActions ? (
					renderChildrenOrChildrenFunction(dialogActions, {
						formId,
						formRef,
						isSubmitting: isSubmitting === true,
						submitEnabled,
					})
				) : (
					<>
						{!smallScreen && !hideCancel ? (
							<FormDialogCancelButton
								className={classes.cancelButton}
								disabled={
									disabled || isSubmitting || DialogProps?.closeDisabled
								}
								color="primary"
								data-testid="form-cancel"
								onClick={handleCancel}
							>
								{cancelLabel}
							</FormDialogCancelButton>
						) : null}
						{!hideSubmit && (
							<FormDialogSubmitButton
								form={formId}
								fullWidth={smallScreen}
								className={classes.submitButton}
								data-testid="form-submit"
								disabled={!submitEnabled}
								label={confirmLabel}
								size={smallScreen ? 'large' : undefined}
							/>
						)}
					</>
				)
			}
			className={clsx(classes.root, className)}
			ownerState={ownerState}
			{...DialogProps}
			title={title ?? DialogProps?.title}
			disableEscapeKeyDown={DialogProps?.disableEscapeKeyDown || isSubmitting}
			disabled={disabled || isSubmitting}
			isWorking={DialogProps?.isWorking || isSubmitting || progress != null}
			open={open}
			preventBackdropClose={DialogProps?.preventBackdropClose || isSubmitting}
			progress={progress}
			onClose={onClose}
		>
			<FormDialogForm
				ref={formRef}
				className={classes.form}
				onSubmit={(event) => event.preventDefault()}
				{...props}
				id={formId}
				sx={{
					padding: disableFormPadding ? 0 : undefined,
				}}
			>
				{children}
			</FormDialogForm>
		</FormDialogRoot>
	);
};

export const FormDialog = FormDialogPrimitive;

export default FormDialog;
